import React from 'react';
import {
  SwipeableDrawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Mic as MicIcon,
  UploadFile as UploadFileIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { NAVIGATION_CONFIG } from '../../config/navigationConfig';
import { useEntityContext } from '../EntityContextSwitch';
import { useUpload } from '../../contexts/UploadContext';
import { useRecorder } from '../../contexts/RecorderContext';

/**
 * CaptureBottomSheet Component
 *
 * A mobile-optimized bottom sheet that displays capture options
 * (Audio, Upload, Notebook) when the user taps the Capture button
 * in the bottom navigation.
 *
 * Features:
 * - Swipe down to dismiss
 * - Tap outside to close
 * - Rounded top corners
 * - Touch-optimized list items (64px min height)
 * - Navigates to routes on selection
 * - Renders AudioRecorderButton which manages its own dialog
 *
 * @param {boolean} open - Controls sheet visibility
 * @param {function} onClose - Called when sheet should close
 */
// eslint-disable-next-line max-lines-per-function
const CaptureBottomSheet = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { currentEntity } = useEntityContext();
  const { triggerUpload } = useUpload();
  const { startRecording, isRecording, toggleExpanded } = useRecorder();

  // Get capture subitems from navigation config
  const captureItem = NAVIGATION_CONFIG.primary.find(item => item.id === 'capture');
  const captureOptions = captureItem?.subitems || [];

  // Map icon names to actual icon components
  const iconMap = {
    MicIcon: MicIcon,
    UploadFileIcon: UploadFileIcon,
    MenuBookIcon: MenuBookIcon,
  };

  const handleOptionClick = async (option) => {
    onClose();

    // Special handling for Audio - use the RecorderContext
    if (option.id === 'audio') {
      if (isRecording) {
        // If already recording, expand the floating recorder
        toggleExpanded();
      } else {
        // Start a new recording
        if (!currentEntity?.id) {
          toast.error('Please select an entity first');
          return;
        }
        await startRecording(currentEntity.id, currentEntity.name);
      }
      return;
    }

    // For Upload, trigger global file upload (no navigation)
    if (option.id === 'upload') {
      setTimeout(() => {
        triggerUpload();
      }, 100);
      return;
    }

    // For other options, navigate normally
    navigate(option.route);
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}} // Required for SwipeableDrawer
      disableSwipeToOpen={false}
      sx={{
        '& .MuiDrawer-paper': {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '80vh',
          backgroundColor: '#FFFFFF',
          color: '#141B2D',
        },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="h6" component="h2">
            Capture
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close capture options"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Options List */}
        <List sx={{ px: 1, pb: 2 }}>
          {captureOptions.map((option) => {
            const IconComponent = iconMap[option.icon];
            return (
              <ListItem key={option.id} disablePadding>
                <ListItemButton
                  onClick={() => handleOptionClick(option)}
                  sx={{
                    minHeight: 64,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon>
                    {IconComponent && (
                      <IconComponent
                        sx={{
                          fontSize: 28,
                          color: 'primary.main',
                        }}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={option.label}
                    secondary={option.description}
                    primaryTypographyProps={{
                      variant: 'body1',
                      fontWeight: 500,
                      color: 'text.primary',
                    }}
                    secondaryTypographyProps={{
                      variant: 'body2',
                      color: 'text.secondary',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </SwipeableDrawer>
  );
};

export default CaptureBottomSheet;
